import React, { useState, useEffect, useMemo, useCallback } from "react"
import { Table, Col, Row, Modal, ModalHeader, ModalBody, ModalFooter, Button, Spinner } from "reactstrap"
import api from "API/Api"
import { Link } from "react-router-dom"
import SweetAlert from "react-bootstrap-sweetalert"
import loader from "./../../assets/loading/loader.gif"
import BulkImportExport from "../../components/BulkImportExport"
import { usePermissions } from "API/PermissionContext"
import { AvForm, AvField, AvRadioGroup, AvRadio } from "availity-reactstrap-validation"

const TAB = { TRANSPORTERS: 1, VEHICLES: 2, ADDITION: 3, DEDUCTION: 4 }
const TAB_LABELS = { 1: "Transporters", 2: "Vehicles", 3: "Vehicle Addition Categories", 4: "Vehicle Deduction Categories" }

const Transporters = () => {
  const { canAdd, canEdit, canDelete, canExport, MODULE } = usePermissions()
  const MID = MODULE.TRANSPORTERS
  const [tab, setTab] = useState(TAB.TRANSPORTERS)
  const [data, setData] = useState([])
  const [transporters, setTransporters] = useState([])
  const [dataLoaded, setDataLoaded] = useState(false)
  const [checkedItems, setCheckedItems] = useState({})
  const [parentChecked, setParentChecked] = useState(false)
  const [confirm_alert, setconfirm_alert] = useState(false)
  const [success_dlg, setsuccess_dlg] = useState(false)
  const [dynamic_title, setdynamic_title] = useState("")
  const [dynamic_description, setdynamic_description] = useState("")
  const [deleteId, setDeleteId] = useState("")
  const [search, setSearch] = useState("")
  const [showFilter, setShowFilter] = useState(false)
  // Modals
  const [addModal, setAddModal] = useState(false)
  const [modalLoading, setModalLoading] = useState(false)

  const apiMap = {
    [TAB.TRANSPORTERS]: { list: "transporters", delete: "delete-transporter/" },
    [TAB.VEHICLES]: { list: "vehicles", delete: "delete-vehicle/" },
    [TAB.ADDITION]: { list: "vehicle-addition-categories", delete: "delete-vehicle-addition-category/" },
    [TAB.DEDUCTION]: { list: "vehicle-deduction-categories", delete: "delete-vehicle-deduction-category/" },
  }

  const fetchData = useCallback(async () => {
    setDataLoaded(false); setCheckedItems({}); setParentChecked(false)
    try {
      const resp = await api.get(apiMap[tab].list)
      setData(Array.isArray(resp.data.data) ? resp.data.data : resp.data.data?.data || [])
    } catch (e) { console.error(e) }
    setDataLoaded(true)
  }, [tab])

  const fetchTransporters = useCallback(async () => {
    try {
      const resp = await api.get("transporters")
      setTransporters(Array.isArray(resp.data.data) ? resp.data.data : resp.data.data?.data || [])
    } catch (e) { console.error(e) }
  }, [])

  useEffect(() => { fetchData() }, [fetchData])
  useEffect(() => { fetchTransporters() }, [fetchTransporters])

  const handleDelete = async () => {
    try { await api.delete(apiMap[tab].delete + deleteId); fetchData() } catch (e) { console.error(e) }
  }

  const handleCheckbox = id => {
    setCheckedItems(prev => {
      const n = { ...prev, [id]: !prev[id] }
      setParentChecked(data.length > 0 && data.every(p => n[p.id]))
      return n
    })
  }
  const handleParentCheckbox = () => {
    const n = !parentChecked; setParentChecked(n)
    const items = {}; data.forEach(p => { items[p.id] = n }); setCheckedItems(items)
  }
  const selectedCount = Object.values(checkedItems).filter(Boolean).length
  const handleMultiDelete = async () => {
    const ids = Object.keys(checkedItems).filter(k => checkedItems[k])
    if (!ids.length) return
    try {
      await Promise.all(ids.map(id => api.delete(apiMap[tab].delete + id)))
      setCheckedItems({}); setParentChecked(false); fetchData()
    } catch (e) { console.error(e) }
  }

  const filtered = useMemo(() => {
    if (!search) return data
    const s = search.toLowerCase()
    return data.filter(d => Object.values(d).some(v => v && String(v).toLowerCase().includes(s)))
  }, [data, search])

  const switchTab = t => { setTab(t); setSearch(""); setShowFilter(false) }

  const actionBtnStyle = bg => ({
    width: "28px", height: "28px", borderRadius: "4px", background: bg, color: "#fff",
    border: "none", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: "12px", cursor: "pointer"
  })

  // ── Modal Submit Handlers ──
  const handleTransporterSubmit = async (e, v) => {
    setModalLoading(true)
    try {
      await api.post("create-transporter", v)
      setAddModal(false); fetchData(); fetchTransporters()
    } catch (err) { console.error(err) }
    setModalLoading(false)
  }
  const handleVehicleSubmit = async (e, v) => {
    setModalLoading(true)
    try {
      await api.post("create-vehicle", v)
      setAddModal(false); fetchData()
    } catch (err) { console.error(err) }
    setModalLoading(false)
  }
  const handleAdditionSubmit = async (e, v) => {
    setModalLoading(true)
    try {
      await api.post("create-vehicle-addition-category", v)
      setAddModal(false); fetchData()
    } catch (err) { console.error(err) }
    setModalLoading(false)
  }
  const handleDeductionSubmit = async (e, v) => {
    setModalLoading(true)
    try {
      await api.post("create-vehicle-deduction-category", v)
      setAddModal(false); fetchData()
    } catch (err) { console.error(err) }
    setModalLoading(false)
  }

  // ── Render Columns per Tab ──
  const renderHead = () => {
    switch (tab) {
      case TAB.TRANSPORTERS: return <><th>Transporter Name</th><th>Phone</th><th>Email</th><th>Payment</th></>
      case TAB.VEHICLES: return <><th>Transporter</th><th>Reg. Number</th><th>Driver</th><th>Category</th><th>Model</th><th>Tanker</th><th>Pickup</th></>
      case TAB.ADDITION: return <th>Category Name</th>
      case TAB.DEDUCTION: return <><th>Advance Category Type</th><th>Category Name</th></>
      default: return null
    }
  }
  const headColSpan = () => {
    switch (tab) {
      case TAB.TRANSPORTERS: return 7
      case TAB.VEHICLES: return 10
      case TAB.ADDITION: return 4
      case TAB.DEDUCTION: return 5
      default: return 5
    }
  }
  const renderRow = (d, i) => {
    switch (tab) {
      case TAB.TRANSPORTERS: return <>
        <td style={{ fontWeight: 600 }}>{d.transporterName}</td>
        <td>{d.phone || "—"}</td>
        <td>{d.email || "—"}</td>
        <td><span style={{ background: d.paymentMode === "account" ? "#6366f115" : "#22c55e15", color: d.paymentMode === "account" ? "#6366f1" : "#22c55e", padding: "2px 8px", borderRadius: "4px", fontSize: "11px", fontWeight: 600 }}>{d.paymentMode === "account" ? "Account" : "Cash"}</span></td>
      </>
      case TAB.VEHICLES: return <>
        <td style={{ fontWeight: 600 }}>{d.transporterName || d.transporter}</td>
        <td><span style={{ fontFamily: "monospace", fontWeight: 600 }}>{d.vehicleNumber}</span></td>
        <td>{d.driverName || "—"}</td>
        <td>{d.vehicleCategory || "—"}</td>
        <td>{d.vehicleModel || "—"}</td>
        <td>{d.isTanker == 1 ? <span style={{ color: "#22c55e", fontWeight: 600 }}>Yes</span> : <span style={{ color: "#94a3b8" }}>No</span>}</td>
        <td>{d.usedForPickup == 1 ? <span style={{ color: "#22c55e", fontWeight: 600 }}>Yes</span> : <span style={{ color: "#94a3b8" }}>No</span>}</td>
      </>
      case TAB.ADDITION: return <td style={{ fontWeight: 600 }}>{d.categoryName}</td>
      case TAB.DEDUCTION: return <>
        <td><span style={{ background: d.categoryType === "advance" ? "#0ea5e915" : "#f59e0b15", color: d.categoryType === "advance" ? "#0ea5e9" : "#f59e0b", padding: "2px 8px", borderRadius: "4px", fontSize: "11px", fontWeight: 600, textTransform: "capitalize" }}>{d.categoryType}</span></td>
        <td style={{ fontWeight: 600 }}>{d.categoryName}</td>
      </>
      default: return null
    }
  }

  const editRoute = tab === TAB.TRANSPORTERS ? "/edit-transporter/" : tab === TAB.VEHICLES ? "/edit-vehicle/" : null

  const summaryCards = [
    { label: `Total ${TAB_LABELS[tab]}`, value: data.length, icon: tab === TAB.TRANSPORTERS ? "fa-truck" : tab === TAB.VEHICLES ? "fa-car" : "fa-th-list", color: "#6366f1" },
  ]

  return (
    <>
      {/* Tab Switcher */}
      <div className="incentivePanel">
        {Object.entries(TAB_LABELS).map(([k, label]) => (
          <span key={k} onClick={() => switchTab(Number(k))}
            className={tab === Number(k) ? "acActive" : ""}
            style={{ cursor: "pointer" }}>{label}</span>
        ))}
      </div>

      <div className="dashBody" style={{ marginTop: "10px" }}>
        <div className="font-size-18 mb-0 dashHeader" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "8px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <h4 style={{ margin: 0 }}>{TAB_LABELS[tab]}</h4>
            {canAdd(MID) && (
              <button title={`Add ${TAB_LABELS[tab]}`} onClick={() => setAddModal(true)}
                style={{ ...actionBtnStyle("#28a745"), textDecoration: "none", width: "32px", height: "32px", fontSize: "14px" }}>
                <i className="fas fa-plus"></i>
              </button>
            )}
            {canDelete(MID) && (
              <button title={selectedCount > 0 ? `Delete ${selectedCount} selected` : "Select items to delete"}
                onClick={() => { if (selectedCount > 0) { handleMultiDelete(); setsuccess_dlg(true); setdynamic_title("Deleted"); setdynamic_description("Items deleted.") } else alert("Select items first.") }}
                style={{ ...actionBtnStyle("#dc3545"), width: "32px", height: "32px", fontSize: "14px", opacity: selectedCount > 0 ? 1 : 0.7 }}>
                <i className="fas fa-trash"></i>
              </button>
            )}
            <button title="Toggle Filter" onClick={() => setShowFilter(!showFilter)}
              style={{ ...actionBtnStyle("#0d6efd"), width: "32px", height: "32px", fontSize: "14px" }}>
              <i className="fas fa-filter"></i>
            </button>
            {(tab === TAB.TRANSPORTERS || tab === TAB.VEHICLES) && (
              <BulkImportExport data={data}
                columns={tab === TAB.TRANSPORTERS ? [
                  { key: "transporterName", label: "Transporter Name", required: true },
                  { key: "phone", label: "Phone" }, { key: "email", label: "Email" },
                  { key: "address", label: "Address" }, { key: "gst", label: "GST" }, { key: "pan", label: "PAN" },
                ] : [
                  { key: "transporter", label: "Transporter ID", required: true },
                  { key: "vehicleNumber", label: "Vehicle Number", required: true },
                  { key: "driverName", label: "Driver Name" }, { key: "driverMobile", label: "Driver Mobile" },
                ]}
                sampleData={tab === TAB.TRANSPORTERS ? [{ transporterName: "ABC Transport", phone: "9876543210" }] : [{ transporter: "1", vehicleNumber: "KA01AB1234" }]}
                apiEndpoint={tab === TAB.TRANSPORTERS ? "create-transporter" : "create-vehicle"}
                entityName={TAB_LABELS[tab]} onImportComplete={fetchData}
                showExport={canExport(MID)} showImport={canAdd(MID)} />
            )}
          </div>
          <div style={{ display: "flex", alignItems: "center" }}>
            <input type="text" className="form-control form-control-sm" placeholder="Search"
              style={{ width: "180px", borderRadius: "4px 0 0 4px" }}
              value={search} onChange={e => setSearch(e.target.value)} />
            <button className="btn btn-primary btn-sm" style={{ borderRadius: "0 4px 4px 0", height: "31px" }}>
              <i className="fas fa-search"></i>
            </button>
          </div>
        </div>

        <Row className="mb-3 mt-2">
          {summaryCards.map((card, i) => (
            <Col md={3} sm={6} key={i}>
              <div style={{
                background: "#fff", borderRadius: "10px", padding: "14px 16px",
                borderLeft: `4px solid ${card.color}`, boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                display: "flex", alignItems: "center", gap: "12px", marginBottom: "8px"
              }}>
                <div style={{ width: "40px", height: "40px", borderRadius: "8px", background: card.color + "15",
                  display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <i className={`fas ${card.icon}`} style={{ color: card.color, fontSize: "16px" }}></i>
                </div>
                <div>
                  <div style={{ fontSize: "20px", fontWeight: "700", color: "#1e293b" }}>{card.value}</div>
                  <div style={{ fontSize: "11px", color: "#6c757d", textTransform: "uppercase", letterSpacing: "0.5px" }}>{card.label}</div>
                </div>
              </div>
            </Col>
          ))}
        </Row>

        <div className="table-responsive">
          <Table className="table mb-0 dairyTbl">
            <thead>
              <tr>
                <th><input type="checkbox" checked={parentChecked} onChange={handleParentCheckbox} /></th>
                <th>#</th>
                {renderHead()}
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {showFilter && (
                <tr>
                  <td></td><td></td>
                  <td colSpan={headColSpan() - 3}>
                    <input type="text" className="form-control form-control-sm" placeholder="Filter..." value={search} onChange={e => setSearch(e.target.value)} />
                  </td>
                  <td></td>
                </tr>
              )}
              {dataLoaded ? (
                filtered.length > 0 ? filtered.map((d, i) => (
                  <tr key={d.id}>
                    <td><input type="checkbox" checked={!!checkedItems[d.id]} onChange={() => handleCheckbox(d.id)} /></td>
                    <td>{i + 1}</td>
                    {renderRow(d, i)}
                    <td>
                      <div style={{ display: "flex", gap: "3px" }}>
                        {canEdit(MID) && editRoute && (
                          <Link title="Edit" to={editRoute + d.id} style={actionBtnStyle("#0d6efd")}>
                            <i className="fas fa-edit"></i></Link>
                        )}
                        {canDelete(MID) && (
                          <button title="Delete" onClick={() => { setDeleteId(d.id); setconfirm_alert(true) }} style={actionBtnStyle("#dc3545")}>
                            <i className="fas fa-trash-alt"></i>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                )) : (
                  <tr><td colSpan={headColSpan()} className="text-center py-4">
                    <i className="fas fa-truck fa-2x text-muted mb-2 d-block"></i>No matching records found
                  </td></tr>
                )
              ) : (
                <tr><td colSpan={headColSpan()}><img className="loaderImg" src={loader} alt="loading" /></td></tr>
              )}
            </tbody>
          </Table>
        </div>
      </div>

      {/* ═══════ ADD MODALS ═══════ */}

      {/* Transporter Modal */}
      {tab === TAB.TRANSPORTERS && (
        <Modal isOpen={addModal} toggle={() => setAddModal(false)} size="md">
          <ModalHeader toggle={() => setAddModal(false)}>Transporters <span style={{ color: "#dc3545", fontSize: "13px" }}>[* required]</span></ModalHeader>
          <ModalBody>
            <AvForm onValidSubmit={handleTransporterSubmit}>
              <div className="mb-3"><AvField name="transporterName" label="Transporter Name *" type="text" required /></div>
              <div className="mb-3"><AvField name="phone" label="Phone" type="text" /></div>
              <div className="mb-3"><AvField name="email" label="Email" type="email" /></div>
              <div className="mb-3"><AvField name="address" label="Address" type="textarea" rows="2" /></div>
              <div className="mb-3"><AvField name="gst" label="GST" type="text" /></div>
              <div className="mb-3"><AvField name="pan" label="PAN" type="text" /></div>
              <div className="mb-3">
                <AvRadioGroup inline name="paymentMode" label="Payment Mode *" required value="cash">
                  <AvRadio label="Cash" value="cash" /><AvRadio label="Account" value="account" />
                </AvRadioGroup>
              </div>
              <div className="mb-3">
                <AvRadioGroup inline name="tdsApplicable" label="TDS Applicable for Billing *" required value="0">
                  <AvRadio label="Yes" value="1" /><AvRadio label="No" value="0" />
                </AvRadioGroup>
              </div>
              <div className="text-end" style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
                <button className="btn btn-success" type="submit" disabled={modalLoading}>
                  {modalLoading ? <Spinner size="sm" /> : "Save Changes"}
                </button>
                <button className="btn btn-danger" type="button" onClick={() => setAddModal(false)}>Close</button>
              </div>
            </AvForm>
          </ModalBody>
        </Modal>
      )}

      {/* Vehicle Modal */}
      {tab === TAB.VEHICLES && (
        <Modal isOpen={addModal} toggle={() => setAddModal(false)} size="lg">
          <ModalHeader toggle={() => setAddModal(false)}>Vehicles <span style={{ color: "#dc3545", fontSize: "13px" }}>[* required]</span></ModalHeader>
          <ModalBody>
            <h6 style={{ color: "#6366f1", marginBottom: "14px", fontSize: "14px" }}><i className="fas fa-car me-2"></i>Vehicles Information</h6>
            <AvForm onValidSubmit={handleVehicleSubmit}>
              <Row>
                <Col md={4}><div className="mb-3">
                  <AvField type="select" name="transporter" label="Transporter *" required>
                    <option value="">Select</option>
                    {transporters.map((t, i) => <option key={i} value={t.id}>{t.transporterName}</option>)}
                  </AvField>
                </div></Col>
                <Col md={4}><div className="mb-3"><AvField name="vehicleNumber" label="Vehicle Registration Number *" type="text" required /></div></Col>
                <Col md={4}><div className="mb-3"><AvField name="driverName" label="Driver Name" type="text" /></div></Col>
                <Col md={4}><div className="mb-3"><AvField name="driverMobile" label="Driver Mobile" type="text" /></div></Col>
                <Col md={4}><div className="mb-3">
                  <AvField type="select" name="vehicleCategory" label="Vehicle Category *" required>
                    <option value="">Select</option>
                    <option value="Truck">Truck</option>
                    <option value="Tanker">Tanker</option>
                    <option value="Van">Van</option>
                    <option value="Auto">Auto</option>
                    <option value="Bike">Bike</option>
                    <option value="Other">Other</option>
                  </AvField>
                </div></Col>
                <Col md={4}><div className="mb-3">
                  <AvField type="select" name="vehicleModel" label="Vehicle Model *" required>
                    <option value="">Select</option>
                    <option value="Tata Ace">Tata Ace</option>
                    <option value="Mahindra Bolero Pickup">Mahindra Bolero Pickup</option>
                    <option value="Ashok Leyland Dost">Ashok Leyland Dost</option>
                    <option value="Eicher">Eicher</option>
                    <option value="Other">Other</option>
                  </AvField>
                </div></Col>
              </Row>
              <Row>
                <Col md={4}><div className="mb-3">
                  <AvRadioGroup inline name="hasInsurance" label="Vehicle has Insurance" value="0">
                    <AvRadio label="Yes" value="1" /><AvRadio label="No" value="0" />
                  </AvRadioGroup>
                </div></Col>
                <Col md={4}><div className="mb-3">
                  <AvRadioGroup inline name="isTanker" label="Vehicle is Used for Milk Transfer (Tanker) *" required value="0">
                    <AvRadio label="Yes" value="1" /><AvRadio label="No" value="0" />
                  </AvRadioGroup>
                </div></Col>
                <Col md={4}><div className="mb-3">
                  <AvRadioGroup inline name="usedForPickup" label="Vehicle Used for Pickup *" required value="0">
                    <AvRadio label="Yes" value="1" /><AvRadio label="No" value="0" />
                  </AvRadioGroup>
                </div></Col>
              </Row>
              <div className="text-end" style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
                <button className="btn btn-success" type="submit" disabled={modalLoading}>
                  {modalLoading ? <Spinner size="sm" /> : "Save Changes"}
                </button>
                <button className="btn btn-danger" type="button" onClick={() => setAddModal(false)}>Close</button>
              </div>
            </AvForm>
          </ModalBody>
        </Modal>
      )}

      {/* Vehicle Addition Category Modal */}
      {tab === TAB.ADDITION && (
        <Modal isOpen={addModal} toggle={() => setAddModal(false)} size="md">
          <ModalHeader toggle={() => setAddModal(false)}>Vehicle Addition Categories <span style={{ color: "#dc3545", fontSize: "13px" }}>[* required]</span></ModalHeader>
          <ModalBody>
            <AvForm onValidSubmit={handleAdditionSubmit}>
              <div className="mb-3"><AvField name="categoryName" label="Client Vehicle Addition Category Name *" type="text" required /></div>
              <div className="text-end" style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
                <button className="btn btn-success" type="submit" disabled={modalLoading}>
                  {modalLoading ? <Spinner size="sm" /> : "Save Changes"}
                </button>
                <button className="btn btn-danger" type="button" onClick={() => setAddModal(false)}>Close</button>
              </div>
            </AvForm>
          </ModalBody>
        </Modal>
      )}

      {/* Vehicle Deduction Category Modal */}
      {tab === TAB.DEDUCTION && (
        <Modal isOpen={addModal} toggle={() => setAddModal(false)} size="md">
          <ModalHeader toggle={() => setAddModal(false)}>Vehicle Deduction Categories <span style={{ color: "#dc3545", fontSize: "13px" }}>[* required]</span></ModalHeader>
          <ModalBody>
            <AvForm onValidSubmit={handleDeductionSubmit}>
              <Row>
                <Col md={6}><div className="mb-3">
                  <AvField type="select" name="categoryType" label="Client Vehicle Advance Category Type *" required>
                    <option value="">Select</option>
                    <option value="advance">Advance</option>
                    <option value="recovery">Recovery</option>
                  </AvField>
                </div></Col>
                <Col md={6}><div className="mb-3"><AvField name="categoryName" label="Client Vehicle Advance Category Name *" type="text" required /></div></Col>
              </Row>
              <div className="text-end" style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
                <button className="btn btn-success" type="submit" disabled={modalLoading}>
                  {modalLoading ? <Spinner size="sm" /> : "Save Changes"}
                </button>
                <button className="btn btn-danger" type="button" onClick={() => setAddModal(false)}>Close</button>
              </div>
            </AvForm>
          </ModalBody>
        </Modal>
      )}

      {confirm_alert && (
        <SweetAlert title="Are you sure?" warning showCancel confirmBtnBsStyle="success" cancelBtnBsStyle="danger"
          onConfirm={() => { handleDelete(); setconfirm_alert(false); setsuccess_dlg(true); setdynamic_title("Deleted"); setdynamic_description("Record deleted.") }}
          onCancel={() => setconfirm_alert(false)}>You won't be able to revert this!</SweetAlert>
      )}
      {success_dlg && (
        <SweetAlert success title={dynamic_title} onConfirm={() => setsuccess_dlg(false)}>{dynamic_description}</SweetAlert>
      )}
    </>
  )
}

export default Transporters
